import React from 'react';
import { CheckIcon, PhotoIcon } from '@heroicons/react/24/outline';

import { NubieAiDirection } from '../../types/api';

interface Props {
  direction: NubieAiDirection;
  selected: boolean;
  onSelect: (slug: string) => void;
  /**
   * Les libelles viennent du PARENT, qui a deja son `t`.
   *
   * Deux raisons, dans cet ordre : un composant de presentation n'a pas a aller
   * chercher ses traductions quand son parent les a sous la main ; et appeler
   * `useTranslation` ici faisait deborder la limite de recursion de TypeScript
   * (TS2589) depuis l'ajout du dix-neuvieme namespace. Passer deux chaines coute
   * moins qu'un cast qui desactiverait le typage des cles.
   */
  labelChosen: string;
  labelSansPhoto: string;
  /** L'apercu est un EXEMPLE, pas du contenu : il se traduit comme le reste. */
  exempleNom: string;
  exempleLieu: string;
  exempleAction: string;
}

/**
 * Une direction artistique.
 *
 * POURQUOI LA PALETTE EST RENDUE ICI ET NON DANS UN APERCU DE PAGE : l'ecran des styles
 * est le plus important du produit, et il doit etre INSTANTANE. Les jetons arrivent avec
 * la direction, donc la carte montre les vraies couleurs sans qu'aucune page soit rendue
 * ni aucun modele appele.
 *
 * Le fond de la carte emploie les couleurs de la direction elle-meme : c'est plus
 * parlant qu'une rangee de pastilles, parce que le commercant voit ce que ca donne en
 * surface plutot qu'en echantillon.
 */
const DirectionCard: React.FC<Props> = ({
  direction,
  selected,
  onSelect,
  labelChosen,
  labelSansPhoto,
  exempleNom,
  exempleLieu,
  exempleAction,
}) => {
  const j = direction.jetons;

  const fond = j['--nubie-fond'] || '#ffffff';
  const encre = j['--nubie-encre'] || '#111111';
  const encreDouce = j['--nubie-encre-doux'] || encre;
  const accent = j['--nubie-accent'] || '#E94B23';
  const trait = j['--nubie-trait'] || '#e5e5e5';
  const titre = j['--nubie-titre'] || 'inherit';

  return (
    <button
      type="button"
      onClick={() => onSelect(direction.slug)}
      aria-pressed={selected}
      className={`group text-left rounded-card overflow-hidden border transition-all ${
        selected
          ? 'border-accent shadow-glow-accent'
          : 'border-border-1 hover:border-border-2 hover:shadow-card-hover'
      }`}
    >
      {/* L'apercu : les vraies couleurs de la direction, pas des pastilles. */}
      <div className="p-5 pb-6" style={{ backgroundColor: fond, borderBottom: `1px solid ${trait}` }}>
        <div className="flex items-start justify-between gap-3">
          <span
            className="text-[10px] uppercase tracking-[0.14em]"
            style={{ color: encreDouce }}
          >
            {direction.nom}
          </span>
          {selected && (
            <span
              className="flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-badge"
              style={{ backgroundColor: accent, color: fond }}
            >
              <CheckIcon className="w-3 h-3" strokeWidth={2.5} />
              {labelChosen}
            </span>
          )}
        </div>

        <p
          className="mt-3 text-2xl leading-tight"
          style={{ color: encre, fontFamily: titre }}
        >
          {exempleNom}
        </p>
        <p className="mt-1 text-xs" style={{ color: encreDouce }}>
          {exempleLieu}
        </p>

        <div className="mt-4 flex items-center gap-2">
          <span
            className="text-[11px] font-semibold px-3 py-1.5 rounded-full"
            style={{ backgroundColor: accent, color: fond }}
          >
            {exempleAction}
          </span>
          <span
            className="text-[11px] px-3 py-1.5 rounded-full"
            style={{ color: encre, border: `1px solid ${trait}` }}
          >
            WhatsApp
          </span>
        </div>
      </div>

      <div className="p-4 bg-surface-1">
        <p className="text-sm text-text-primary font-medium">{direction.raison}</p>
        <p className="mt-2 flex items-start gap-1.5 text-xs text-text-secondary">
          <PhotoIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-text-tertiary" strokeWidth={1.6} />
          <span>
            <span className="font-medium text-text-secondary">{labelSansPhoto} — </span>
            {direction.strategie_sans_photo}
          </span>
        </p>
      </div>
    </button>
  );
};

export default DirectionCard;
